import React, { useState } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { api, BACKEND_URL } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Upload, 
  Download, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files?.[0]) {
      validateAndSetFile(files[0]);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFile(file);
    setResult(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(res.data);
      
      if (res.data.success) {
        toast.success(`Imported ${res.data.imported_count} bids successfully`);
      } else {
        toast.warning('Upload completed with errors');
      }
    } catch (error) {
      toast.error('Upload failed');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${BACKEND_URL}/api/upload/template/download`, '_blank');
  };

  return (
    <AppLayout>
      <div className="space-y-6 max-w-3xl" data-testid="upload-container">
        {/* Header */}
        <div>
          <h1 className="page-title">Upload Bids</h1>
          <p className="text-slate-500">Import your historical bid data from CSV</p>
        </div>

        {/* Template Download */}
        <Card className="card-industrial">
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Download CSV Template</h3>
                <p className="text-sm text-slate-500">
                  Use our template to ensure your data is formatted correctly
                </p>
              </div>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="section-title text-lg">Upload CSV File</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`
                relative border-2 border-dashed rounded-sm p-8 text-center transition-colors
                ${dragActive 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-slate-300 hover:border-slate-400'}
                ${file ? 'bg-slate-50' : ''}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-orange-500" />
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }}
                    className="ml-4"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-2">
                    Drag and drop your CSV file here, or click to browse
                  </p>
                  <p className="text-sm text-slate-400">
                    Only CSV files are supported
                  </p>
                </>
              )}
            </div>

            {file && !result && (
              <Button
                onClick={handleUpload}
                className="w-full mt-4 btn-primary"
                disabled={uploading}
                data-testid="upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload and Import
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <Card className={`card-industrial ${result.success ? 'border-green-200' : 'border-orange-200'}`}>
            <CardHeader>
              <CardTitle className={`section-title text-lg flex items-center gap-2 ${
                result.success ? 'text-green-700' : 'text-orange-700'
              }`}>
                {result.success ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                Upload {result.success ? 'Successful' : 'Completed with Errors'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-slate-50 rounded-sm">
                  <p className="text-2xl font-mono font-bold text-slate-900">{result.total_rows}</p>
                  <p className="text-sm text-slate-500">Total Rows</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-sm">
                  <p className="text-2xl font-mono font-bold text-green-600">{result.imported_count}</p>
                  <p className="text-sm text-slate-500">Imported</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-sm">
                  <p className="text-2xl font-mono font-bold text-orange-600">{result.skipped_count}</p>
                  <p className="text-sm text-slate-500">Skipped</p>
                </div>
              </div>

              {result.errors?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Errors</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {result.errors.map((error, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm p-2 bg-red-50 rounded-sm">
                        <span className="font-mono text-red-600">Row {error.row}:</span>
                        <span className="text-red-700">{error.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => { setFile(null); setResult(null); }}
                className="mt-4"
              >
                Upload Another File
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="card-industrial bg-slate-50">
          <CardContent className="py-6">
            <h3 className="font-semibold text-slate-900 mb-4">CSV Format Requirements</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500">•</span>
                <span><strong>Required columns:</strong> bid_ref, client_name, submitted_price</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500">•</span>
                <span><strong>Optional columns:</strong> project_type, location, tender_due_date, status, labour_hours, labour_rate, materials_cost, subcontract_cost, equipment_cost, overhead_pct, contingency_pct, outcome</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500">•</span>
                <span><strong>Date format:</strong> YYYY-MM-DD</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500">•</span>
                <span><strong>Status values:</strong> pending, won, lost, unknown</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500">•</span>
                <span><strong>Outcome values:</strong> won, lost (or leave empty)</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
